/**
 * «Asignación inicial» — the parameters a run reads, by their name and their value in words (R1/R3: a planner reads «Retención
 * comparada: 0, 20, 30, 40 %», never a registry key).
 *
 * The name is the register's own (`parameterName`, the card's label): the nine keys of the app have a card since the final round
 * (ADR 9025 amended 2026-11-03), so the app carries no names of its own — two sources of text for one thing is how a screen and the
 * register come to disagree. A key with no card falls back to what `parameterName` does.
 */

import type { TFunction } from 'i18next';

import { formatInteger, formatNumber, formatPercent } from '@/lib/format';
import { parameterName } from '@/planning';

import type { ReasonWords } from './reasons';
import { SIMILARITY_BASIS_KEY, wordsOf } from './words';

/** A translator of the shape the register's `parameterName` and the export writers hold. */
export type Say = (key: string, values?: Readonly<Record<string, unknown>>) => string;

/** The parameter's name in the app. Pure. */
export function paramName(say: Say, key: string): string {
  return parameterName(say, key);
}

/**
 * What a refused run or list found missing, in words: the platform's own label for an input it names (`missingInput`: the price, the cost,
 * the quantity bought…); a registry parameter it names instead keeps the register's name in the app. Joined by «; ». Pure.
 */
export function missingInputWords(say: Say, reasons: ReasonWords, keys: readonly string[]): string {
  return keys.map((key) => {
    const platform = reasons.missing(key);
    return platform !== null && platform !== '' ? platform : paramName(say, key);
  }).join('; ');
}

/** A value read from the run's record: a number, a text, or nothing. */
export type ParameterValue = string | number | null | undefined;

/** The hold-back fractions «0,0.2,0.3,0.4» as «0, 20, 30, 40 %». Pure; a list that does not parse is said as stated. */
export function holdbackWords(value: string, language: string): string {
  const parts = value.split(',').map((part) => part.trim()).filter((part) => part !== '');
  const fractions = parts.map(Number);
  if (fractions.length === 0 || fractions.some((one) => !Number.isFinite(one))) return value;
  return `${fractions.map((one) => formatNumber(Math.round(one * 10_000) / 100, language)).join(', ')} %`;
}

/** A parameter's value in words: a share as a percentage, a count with its unit, a basis by its name. Pure. */
export function parameterValueWords(t: TFunction, key: string, value: ParameterValue, language: string): string {
  if (value === null || value === undefined || value === '') return t('absent.notStated');
  const number = typeof value === 'number' ? value : Number(value);
  switch (key) {
    case 'launch.initial.holdback_scenarios': return holdbackWords(String(value), language);
    case 'launch.initial.recapture_rate': return Number.isFinite(number) ? formatPercent(number, language, 0) : String(value);
    case 'launch.initial.similarity_basis': return wordsOf(t, SIMILARITY_BASIS_KEY, String(value)) ?? t('launch.initial.unclassified');
    case 'launch.initial.approved_line_hold_days': return Number.isFinite(number) ? t('launch.initial.paramValue.days', { count: number, figure: formatInteger(number, language) }) : String(value);
    case 'launch.initial.history_weeks':
    case 'launch.initial.recent_arrival_weeks': return Number.isFinite(number) ? t('launch.initial.paramValue.weeks', { count: number, figure: formatInteger(number, language) }) : String(value);
    case 'launch.initial.new_article_max_stores': return Number.isFinite(number) ? t('launch.initial.paramValue.stores', { count: number, figure: formatInteger(number, language) }) : String(value);
    case 'launch.initial.handover_enabled': return String(value) === 'true' || String(value) === '1' ? t('launch.initial.paramValue.on') : t('launch.initial.paramValue.off');
    default: return typeof value === 'number' ? formatNumber(value, language) : String(value);
  }
}

/** A market's name in the reader's language — `CO` → «Colombia» — or the code where the runtime has none. Pure. */
function marketName(code: string, language: string): string {
  try {
    return new Intl.DisplayNames([language], { type: 'region', fallback: 'code' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** Where a parameter's value applies, in words: the whole estate, or one market by its name. Pure. */
export function scopeWords(t: TFunction, scope: string | null | undefined, language: string): string {
  if (scope === null || scope === undefined) return t('absent.notStated');
  return scope === 'estate' || scope === '' ? t('launch.initial.parameters.scopeEstate') : marketName(scope, language);
}
